/**
 * The single place the SPA talks to the API.
 *
 * `credentials: 'include'` on every call: the session lives in an httpOnly
 * cookie scoped to the parent domain, so the browser must be told to send it
 * cross-subdomain (app. -> api.). There is no token in JavaScript to steal.
 */

import type {
  InsightPeriod,
  StockInsights,
  TreatmentProfitReport,
  UsagePurpose as CoreUsagePurpose,
} from '@inventory-tracker/core';

const BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      requestId?: string;
      requiredPlan?: string;
      feature?: string;
    };
    /**
     * 402 with a `requiredPlan` is the plan gate, not a failure. Given its own
     * type so a locked page can offer the upgrade instead of rendering an
     * error — the API answers 402 rather than 403 precisely to make that
     * distinction possible (apps/api/src/lib/planGate.ts).
     *
     * `/team/invite` also answers 402 for seat limits, but it bypasses this
     * helper and handles its own response, so the two do not collide.
     */
    if (res.status === 402 && body.requiredPlan) {
      throw new PlanRequiredError(
        body.error ?? 'A higher plan is needed',
        body.requiredPlan as Plan,
        body.feature ?? '',
      );
    }
    throw new ApiError(res.status, body.error ?? res.statusText, body.requestId);
  }

  /**
   * A 204 has no body, and `Response.json()` on an empty body throws
   * "Unexpected end of JSON input" — a parse error, reported to the user as
   * though the request had failed, on a request that in fact succeeded.
   *
   * Every mutation that returns 204 hits this: editing a product, renaming a
   * classification, deleting a location. It stayed hidden while the SPA only
   * ever called endpoints that return a body.
   *
   * The 205 and the content-length check are belt and braces — a proxy or a
   * middleware that strips a body would produce the same crash otherwise.
   */
  if (res.status === 204 || res.status === 205 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await res.json()) as T;
}

/**
 * The plan gate refused. Carries what the API already worked out, so no screen
 * has to re-derive which plan unlocks which feature.
 */
export class PlanRequiredError extends Error {
  constructor(
    message: string,
    readonly requiredPlan: Plan,
    readonly feature: string,
  ) {
    super(message);
    this.name = 'PlanRequiredError';
  }
}

export interface HealthResponse {
  status: string;
  release: string;
  uptimeSeconds: number;
}

export const getHealth = () => api<HealthResponse>('/health');

// ---------------------------------------------------------------------- auth

export type Plan = 'starter' | 'pro' | 'premium';
export type Cycle = 'monthly' | 'yearly';
export type Role = 'owner' | 'manager' | 'staff';

export type DigestFrequency = 'off' | 'weekly' | 'daily';

export interface ContactDetails {
  phone?: string;
  website?: string;
  line1?: string;
  line2?: string;
  city?: string;
  postcode?: string;
  companyNumber?: string;
  vatNumber?: string;
}

export interface Me {
  user: {
    id: string;
    email: string;
    name: string;
    role: Role;
    /** How often this person gets the expiry digest. Their own setting. */
    digestFrequency: DigestFrequency;
  };
  account: {
    id: string;
    clinicName: string;
    tagline: string | null;
    /** Basis points — 20% is 2000. Converted for display, never stored as a float. */
    vatRateBps: number;
    timeZone: string;
    /** Null until the guided setup is finished or skipped. */
    onboardedAt: string | null;
    /** Null when the clinic has no logo; otherwise changes whenever it does. */
    logoVersion: string | null;
    /** Phone, website, address, company and VAT numbers. Absent keys are unset. */
    contact: ContactDetails;
  };
  subscription: {
    plan: Plan;
    cycle: Cycle;
    status: string;
    trialEndsAt: string | null;
    /** The trial or subscription has ended: everything is visible, nothing can change. */
    readOnly: boolean;
    readOnlyReason: 'trial_ended' | 'subscription_ended' | null;
  };
}

export interface SignupInput {
  clinicName: string;
  ownerName: string;
  email: string;
  password: string;
  plan: Plan;
  cycle: Cycle;
}

/**
 * 401 is the normal answer for a signed-out visitor, not an error worth
 * throwing — the app boots by asking this question and expects "no" half the
 * time. Anything else still throws.
 */
export async function getMe(): Promise<Me | null> {
  try {
    return await api<Me>('/auth/me');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export const signup = (input: SignupInput) =>
  api<{ user: Me['user']; account: Me['account']; plan: Plan }>('/auth/signup', {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const login = (email: string, password: string) =>
  api<{ user: Me['user'] }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

/** 204, so there is no body to parse — `api()` would choke on the empty response. */
/**
 * Business profile. Every field optional — this is a PATCH, so a form that only
 * changed the tagline does not resend the clinic name, and the API only audits
 * what actually moved.
 */
export async function updateAccount(input: {
  clinicName?: string;
  tagline?: string | null;
  vatRateBps?: number;
  timeZone?: string;
}): Promise<{ account: Me['account'] }> {
  return api('/account', { method: 'PATCH', body: JSON.stringify(input) });
}

/**
 * Changing your own password ends every other session. The API re-issues a
 * cookie for this device in the same response, so there is nothing to do here
 * beyond letting the browser take it.
 */
export async function changePassword(currentPassword: string, password: string): Promise<void> {
  await api('/account/password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, password }),
  });
}

// ------------------------------------------------------------ AI assistant

export type AiKind = 'social' | 'email';

export interface AiAllowance {
  limit: number;
  used: number;
  remaining: number;
  /** ISO date the monthly allowance resets on. */
  resetsOn: string;
}

export interface AiGeneration {
  id: string;
  kind: AiKind;
  text: string;
  createdAt: string;
}

export interface AiAssistantStatus {
  /** False until the service is configured. The card hides itself on false. */
  available: boolean;
  mayGenerate: boolean;
  allowance: AiAllowance;
  recent: AiGeneration[];
}

export async function getAiAssistant(): Promise<AiAssistantStatus> {
  return api('/ai/assistant');
}

export async function generateMarketing(
  kind: AiKind,
): Promise<{ generation: AiGeneration; allowance: AiAllowance }> {
  return api('/ai/assistant', { method: 'POST', body: JSON.stringify({ kind }) });
}

export async function saveContactDetails(
  contact: ContactDetails,
): Promise<{ contact: ContactDetails }> {
  return api('/account/contact', { method: 'PUT', body: JSON.stringify(contact) });
}

export async function uploadLogo(
  contentType: string,
  dataBase64: string,
): Promise<{ logoVersion: string }> {
  return api('/account/logo', { method: 'PUT', body: JSON.stringify({ contentType, dataBase64 }) });
}

export async function removeLogo(): Promise<void> {
  await api('/account/logo', { method: 'DELETE' });
}

/**
 * The logo's bytes, as a Blob. Fetched rather than pointed at with `<img src>`:
 * the image sits behind the session cookie on another origin (`api.`), and a
 * credentialed `fetch` goes through the CORS policy this app already has,
 * where a plain image request would be subject to the API's
 * Cross-Origin-Resource-Policy header instead. `version` is only a cache key.
 */
export async function fetchLogo(version: string): Promise<Blob> {
  const res = await fetch(`${BASE}/account/logo?v=${encodeURIComponent(version)}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new ApiError(res.status, 'Could not load the logo');
  return res.blob();
}

/** Your own expiry-email frequency. Nobody else's is touched. */
export async function setDigestFrequency(
  digestFrequency: DigestFrequency,
): Promise<{ digestFrequency: DigestFrequency }> {
  return api('/account/notifications', {
    method: 'PUT',
    body: JSON.stringify({ digestFrequency }),
  });
}

/**
 * The link in a digest email. Authorised by the token, not a session — the
 * person clicking it is often signed out, so this deliberately does not go
 * through `api()` and its 401 handling.
 */
export async function unsubscribeFromDigest(
  token: string,
  resubscribe = false,
): Promise<{ digestFrequency: DigestFrequency }> {
  const res = await fetch(`${BASE}/notifications/unsubscribe`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token, resubscribe }),
  });
  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    digestFrequency?: DigestFrequency;
  };
  if (!res.ok || !body.digestFrequency) {
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
  return { digestFrequency: body.digestFrequency };
}

export interface AuditEntry {
  /** bigserial from the API — a string, because it does not fit a JS number. */
  seq: string;
  category: string;
  description: string;
  detail: Record<string, unknown> | null;
  createdAt: string;
  user: { id: string; name: string } | null;
}

export interface AuditFilters {
  category?: string;
  userId?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

/**
 * Paged by cursor rather than page number: the log is append-only and grows
 * while it is being read, so an offset would repeat and skip entries.
 */
export async function getAudit(
  filters: AuditFilters = {},
): Promise<{ entries: AuditEntry[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return api(`/audit${qs ? `?${qs}` : ''}`);
}

/** One-way latch: finishing and skipping the wizard are the same to the app. */
export const markOnboarded = () => api<{ ok: true }>('/account/onboarded', { method: 'POST' });

export async function logout(): Promise<void> {
  await fetch(`${BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
}

export const forgotPassword = async (email: string): Promise<void> => {
  // Always 204, even for an unknown address — the API refuses to reveal whether
  // an account exists, so there is nothing to branch on here either.
  await fetch(`${BASE}/auth/forgot-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
};

/** 204 on success; throws ApiError with a readable message on a spent token. */
export async function resetPassword(token: string, password: string): Promise<void> {
  const res = await fetch(`${BASE}/auth/reset-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
}

export async function verifyEmail(token: string): Promise<void> {
  const res = await fetch(`${BASE}/auth/verify-email`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
}

export async function resendVerification(): Promise<void> {
  await fetch(`${BASE}/auth/resend-verification`, { method: 'POST', credentials: 'include' });
}

// ---------------------------------------------------------------------- team

export type JobTitle =
  'owner' | 'manager' | 'injector' | 'front' | 'operations' | 'admin' | 'other';

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: Role;
  jobTitle: JobTitle;
  status: 'invited' | 'active' | 'deactivated';
  emailVerifiedAt: string | null;
  invitedAt: string | null;
  lastLoginAt: string | null;
  hasPin: boolean;
}

export interface TeamResponse {
  members: TeamMember[];
  /** `limit: null` means unlimited — JSON cannot carry Infinity. */
  seats: { used: number; limit: number | null; canInviteMore: boolean };
}

export const getTeam = () => api<TeamResponse>('/team');

/**
 * Thrown when the plan is out of seats. Carried as its own type so the UI can
 * offer an upgrade rather than showing a generic error — the API answers 402
 * precisely to make that distinction possible.
 */
export class SeatLimitError extends Error {
  constructor(
    message: string,
    readonly limit: number,
  ) {
    super(message);
    this.name = 'SeatLimitError';
  }
}

export async function inviteMember(input: {
  name: string;
  email: string;
  role: Role;
  jobTitle: JobTitle;
}): Promise<void> {
  const res = await fetch(`${BASE}/team/invite`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (res.status === 402) {
    const body = (await res.json()) as { error: string; limit: number };
    throw new SeatLimitError(body.error, body.limit);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
}

async function teamAction(path: string, init: RequestInit = {}): Promise<void> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
}

export const changeRole = (userId: string, role: Role, jobTitle: JobTitle) =>
  teamAction(`/team/${userId}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ role, jobTitle }),
  });

export const deactivateMember = (userId: string) =>
  teamAction(`/team/${userId}/deactivate`, { method: 'POST' });

export const reactivateMember = (userId: string) =>
  teamAction(`/team/${userId}/reactivate`, { method: 'POST' });

export const resendInvite = (userId: string) =>
  teamAction(`/team/${userId}/resend-invite`, { method: 'POST' });

/** Activating an invitation. Signs you in, so no separate login step. */
export async function activateAccount(
  token: string,
  password: string,
  name?: string,
): Promise<void> {
  const res = await fetch(`${BASE}/auth/activate`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token, password, ...(name ? { name } : {}) }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
}

// ------------------------------------------------------------ PIN quick-switch

export interface SwitchableUser {
  id: string;
  name: string;
  jobTitle: JobTitle;
  role: Role;
  isCurrent: boolean;
  /** ISO timestamp while locked out, else null. */
  lockedUntil: string | null;
}

export const getSwitchable = () => api<{ users: SwitchableUser[] }>('/auth/switchable');

export const setPin = (pin: string) =>
  teamAction('/auth/pin', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ pin }),
  });

export const clearPin = () => teamAction('/auth/pin', { method: 'DELETE' });

export const unlockPin = (userId: string) =>
  teamAction(`/team/${userId}/unlock-pin`, { method: 'POST' });

/** Thrown when the target's PIN is locked, so the UI can say so specifically. */
export class PinLockedError extends Error {
  constructor(
    message: string,
    readonly lockedUntil: string | null,
  ) {
    super(message);
    this.name = 'PinLockedError';
  }
}

export async function pinSwitch(userId: string, pin: string): Promise<void> {
  const res = await fetch(`${BASE}/auth/pin/switch`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ userId, pin }),
  });
  if (res.status === 429) {
    const body = (await res.json()) as { error: string; lockedUntil?: string };
    throw new PinLockedError(body.error, body.lockedUntil ?? null);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
}

// ----------------------------------------------------------------- inventory

/**
 * Status keys mirror core's. They arrive from the API already computed, so the
 * SPA never classifies stock itself — one definition, server-side.
 */
export type StockStatusKey = 'out' | 'reorder' | 'low' | 'ok';
export type ExpiryStatusKey = 'expired' | 'urgent' | 'soon' | 'fresh' | 'na';

export interface ProductStatus {
  stock: StockStatusKey;
  stockLabel: string;
  expiry: ExpiryStatusKey;
  expiryLabel: string;
  expiryWeeks: number;
  expiryDate: string;
}

export interface ProductRow {
  id: string;
  name: string;
  brand: string | null;
  category: string | null;
  classificationLabel: string | null;
  /**
   * `GET /products` has always returned these two; the type simply did not
   * declare them, so nothing could edit a product without re-fetching it.
   * Manage stock needs the id to move a product between classifications and the
   * price to edit it in place.
   */
  classificationId: string | null;
  retailPricePence: number | null;
  minLevel: number;
  unitLabel: string | null;
  /** What one pack holds, in hundredths; 100 when it is used whole. */
  packContentsHundredths?: number;
  contentUnit?: string | null;
  /** Left in an opened pack, in hundredths. */
  openHundredths?: number;
  unitCostPence: number;
  qtyOnHand: number;
  stockValuePence: number;
  earliestExpiry: string | null;
  batchCount: number;
  status: ProductStatus;
}

export interface DashboardResponse {
  today: string;
  kpis: {
    stockValuePence: number;
    totalUnits: number;
    productCount: number;
    reorderCount: number;
    expiringCount: number;
    healthyCount: number;
  };
  alert: { expiredProducts: number; shouldAlert: boolean };
  cards: {
    reorder: { total: number; products: ProductRow[] };
    expiring: { total: number; products: ProductRow[] };
  };
}

export const getDashboard = () => api<DashboardResponse>('/dashboard');

export interface ProductListResponse {
  products: ProductRow[];
  capacity: { used: number; limit: number | null; canAddMore: boolean };
}

export const listProducts = (query = '') => api<ProductListResponse>(`/products${query}`);

export interface Batch {
  id: string;
  lot: string | null;
  expiry: string | null;
  receivedOn: string;
  qtyReceived: number;
  qtyRemaining: number;
  unitCostPence: number;
  locationLabel: string | null;
  expiryStatus: ExpiryStatusKey;
  expiryLabel: string;
  valuePence: number;
}

export interface Movement {
  id: string;
  groupId: string | null;
  batchId: string | null;
  lot: string | null;
  type: 'in' | 'out' | 'waste' | 'adjustment';
  qty: number;
  reason: string | null;
  note: string | null;
  pricePence: number | null;
  voidsMovementId: string | null;
  occurredOn: string;
  createdAt: string;
  byName: string | null;
  isVoided: boolean;
}

export interface ProductDetail {
  today: string;
  product: ProductRow & {
    retailPricePence: number | null;
    isRetail: boolean | null;
    notes: string | null;
  };
  batches: Batch[];
  movements: Movement[];
  /** May this person edit the product and correct its batches (manager and up). */
  mayEdit: boolean;
  /** The offer running on this product today, for the sale form. */
  promotion: { id: string; offer: string; endsOn: string | null; pricePence: number } | null;
  /** The last throw-away of the open pack, while it can still be undone. */
  lastDiscard: {
    id: string;
    amountHundredths: number;
    valuePence: number;
    reason: string;
    occurredOn: string;
  } | null;
}

export const getProduct = (id: string) => api<ProductDetail>(`/products/${id}`);

/**
 * Why stock left the shelf. These are core's `USAGE_PURPOSES`, which is what
 * the API validates against. Until 13 October 2026 this type said `'retail'`
 * and `'sample'`, which the API has never accepted: choosing "Retail sale" or
 * "Sample" in the drawer was refused with a 400. Taking the type from core
 * means the two cannot drift again.
 */
export type UsagePurpose = CoreUsagePurpose;

export interface LogUsageResult {
  groupId: string;
  qty: number;
  purpose: UsagePurpose;
  allocations: { batchId: string; lot: string | null; qty: number }[];
}

export const logUsage = (
  productId: string,
  body: {
    qty: number;
    purpose: UsagePurpose;
    note?: string;
    pricePence?: number | null;
    /** Take from this batch instead of the one that expires first. */
    batchId?: string;
  },
) =>
  api<LogUsageResult>(`/products/${productId}/log`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** Fix a batch's lot number or expiry after it was booked in wrong. Audited. */
export const updateBatch = (batchId: string, body: { lot?: string | null; expiry?: string | null }) =>
  api<void>(`/batches/${batchId}`, { method: 'PATCH', body: JSON.stringify(body) });

/** Throw away what is left in a product's open pack. */
export const discardOpenPack = (productId: string, reason: string) =>
  api<{ amountHundredths: number; valuePence: number }>(`/products/${productId}/discard-open`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });

export const undoDiscardOpenPack = (productId: string) =>
  api<{ amountHundredths: number }>(`/products/${productId}/discard-open/undo`, {
    method: 'POST',
    body: '{}',
  });

export const voidMovement = (movementId: string, note?: string) =>
  api<{ voided: boolean; reversedRows: number; qty: number }>(`/movements/${movementId}/void`, {
    method: 'POST',
    body: JSON.stringify(note ? { note } : {}),
  });

// --------------------------------------------------------------- stock counts

export type VarianceReason = 'miscount' | 'gift' | 'lost' | 'breakage' | 'expired' | 'other';

export interface StockCountLine {
  productId: string;
  countedQty: number;
  expectedQty?: number;
  reason?: VarianceReason;
  reasonNote?: string;
  foundExpiry?: string | null;
}

export interface StockCountResult {
  applied: boolean;
  groupId: string | null;
  countedLines: number;
  adjustedLines: number;
  lines: {
    productId: string;
    productName: string;
    systemQty: number;
    countedQty: number;
    variance: number;
    reasonLabel: string | null;
  }[];
}

/** Thrown when stock moved between drawing the count sheet and applying it. */
export class StaleCountError extends Error {
  constructor(
    message: string,
    readonly stale: {
      productId: string;
      productName: string;
      expectedQty: number;
      actualQty: number;
    }[],
  ) {
    super(message);
    this.name = 'StaleCountError';
  }
}

export async function applyStockCount(
  lines: StockCountLine[],
  note?: string,
): Promise<StockCountResult> {
  const res = await fetch(`${BASE}/stock-count`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(note ? { lines, note } : { lines }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      reason?: string;
      stale?: StaleCountError['stale'];
    };
    if (body.reason === 'stale_count') {
      throw new StaleCountError(body.error ?? 'Stock moved while counting', body.stale ?? []);
    }
    throw new ApiError(res.status, body.error ?? res.statusText);
  }

  return (await res.json()) as StockCountResult;
}

// -------------------------------------------------------------------- import

export interface ImportRow {
  name: string;
  brand?: string;
  category?: string;
  classification?: string;
  qty?: number;
  unitCostPence?: number;
  retailPricePence?: number;
  lot?: string;
  expiry?: string;
  minLevel?: number;
}

export interface ImportResult {
  imported: number;
  skipped: number;
  rows: number;
  unitsAdded: number;
  skippedRows: {
    name: string;
    reason: 'duplicate_in_file' | 'already_exists' | 'over_plan_limit';
  }[];
  limitReached?: { plan: Plan; limit: number; notImported: number };
}

export const importProducts = (rows: ImportRow[], fileName?: string) =>
  api<ImportResult>('/products/import', {
    method: 'POST',
    body: JSON.stringify(fileName ? { rows, fileName } : { rows }),
  });

// ------------------------------------------------------------------ taxonomy

export interface Classification {
  id: string;
  label: string;
  color: string | null;
  isRetail: boolean;
  sortOrder: number;
}

export interface StockLocation {
  id: string;
  label: string;
}

export const listClassifications = () =>
  api<{ classifications: Classification[] }>('/classifications');

export const listLocations = () => api<{ locations: StockLocation[] }>('/locations');

/**
 * Taxonomy and product editing — all manager-and-up, enforced by the API.
 *
 * `key` is absent from the update calls on purpose: it is immutable server-side
 * because Excel imports and the prototype's `product.type` match on it, so
 * offering it here would be offering an edit that is always refused.
 */
export const createClassification = (input: { label: string; isRetail?: boolean }) =>
  api<{ id: string; key: string; label: string }>('/classifications', {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const updateClassification = (id: string, input: { label?: string; isRetail?: boolean }) =>
  api<void>(`/classifications/${id}`, { method: 'PATCH', body: JSON.stringify(input) });

/** 409 when products still reference it — the message names the count. */
export const deleteClassification = (id: string) =>
  api<void>(`/classifications/${id}`, { method: 'DELETE' });

export const createLocation = (input: { label: string }) =>
  api<{ id: string; key: string; label: string }>('/locations', {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const updateLocation = (id: string, input: { label: string }) =>
  api<void>(`/locations/${id}`, { method: 'PATCH', body: JSON.stringify(input) });

export const deleteLocation = (id: string) => api<void>(`/locations/${id}`, { method: 'DELETE' });

export interface UpdateProductInput {
  name?: string;
  brand?: string | null;
  category?: string | null;
  classificationId?: string;
  unitCostPence?: number;
  retailPricePence?: number | null;
  minLevel?: number;
  unitLabel?: string;
  packContentsHundredths?: number;
  contentUnit?: string | null;
}

export const updateProduct = (id: string, input: UpdateProductInput) =>
  api<void>(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(input) });

/**
 * Archive, not delete. History keeps its product — a movement whose product row
 * vanished is an audit trail that cannot answer "what was it".
 */
export const archiveProduct = (id: string) =>
  api<void>(`/products/${id}/archive`, { method: 'POST' });

export const restoreProduct = (id: string) =>
  api<void>(`/products/${id}/restore`, { method: 'POST' });

// --------------------------------------------------------------- adding stock

export interface AddStockInput {
  qty: number;
  lot?: string;
  expiry?: string | null;
  locationId?: string;
  unitCostPence?: number;
  receivedOn?: string;
}

export const addStock = (productId: string, body: AddStockInput) =>
  api<{ batchId: string; qty: number }>(`/products/${productId}/stock`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export interface CreateProductInput {
  name: string;
  brand?: string;
  category?: string;
  classificationId?: string;
  unitCostPence?: number;
  retailPricePence?: number | null;
  minLevel?: number;
  unitLabel?: string;
  packContentsHundredths?: number;
  contentUnit?: string | null;
}

export const createProduct = (body: CreateProductInput) =>
  api<{ id: string; name: string }>('/products', {
    method: 'POST',
    body: JSON.stringify(body),
  });

// -------------------------------------------------------------------- expiry

export type ExpiryBucket = 'expired' | 'urgent' | 'soon' | 'all';

export interface ExpiryBatch {
  batchId: string;
  lot: string | null;
  expiry: string;
  qtyRemaining: number;
  unitCostPence: number;
  productId: string;
  productName: string;
  unitLabel: string | null;
  locationLabel: string | null;
  expiryStatus: ExpiryStatusKey;
  weeksLeft: number;
  valuePence: number;
}

export interface ExpiryResponse {
  today: string;
  thresholds: { urgentWeeks: number; soonWeeks: number };
  batches: ExpiryBatch[];
  summary: Record<'expired' | 'urgent' | 'soon', { batches: number; valuePence: number }>;
}

export const getExpiry = (query = '') => api<ExpiryResponse>(`/expiry${query}`);

export type WriteOffReason = 'expired' | 'damaged' | 'contaminated' | 'recalled' | 'lost' | 'other';

export const writeOffBatch = (
  batchId: string,
  body: { qty?: number; reason: WriteOffReason; note?: string },
) =>
  api<{ writtenOff: number; valuePence: number }>(`/batches/${batchId}/write-off`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export interface WriteOffEntry {
  movementId: string;
  qty: number;
  reason: string;
  note: string | null;
  occurredOn: string;
  productId: string;
  productName: string;
  lot: string | null;
  expiry: string | null;
  unitCostPence: number | null;
  byName: string | null;
  valuePence: number;
}

export interface WriteOffReport {
  entries: WriteOffEntry[];
  total: { qty: number; valuePence: number };
  byReason: Record<string, { qty: number; valuePence: number }>;
}

export const getWriteOffs = (query = '') => api<WriteOffReport>(`/write-offs${query}`);

// ------------------------------------------------------------------ movements

export interface MovementRow extends Movement {
  productId: string;
  productName: string | null;
}

export const listMovements = (query = '') =>
  api<{ movements: MovementRow[] }>(`/movements${query}`);

// --------------------------------------------------------------- promotions

export type PromotionKind = 'percent' | 'amount';
export type PromotionStatusKey = 'scheduled' | 'active' | 'ended';

export interface Promotion {
  id: string;
  productId: string;
  productName: string;
  retailPricePence: number | null;
  kind: PromotionKind;
  value: number;
  startsOn: string;
  endsOn: string | null;
  note: string | null;
  status: PromotionStatusKey;
  statusLabel: string;
  /** What a customer pays while it runs. Null if the product has no price. */
  promoPricePence: number | null;
  savingPence: number | null;
}

export interface PromotionSuggestion {
  productId: string;
  productName: string;
  reason: 'expiring' | 'not_selling' | 'overstocked';
  flag: string;
  why: string;
  offer: string;
  prefill: { kind: PromotionKind; value: number; endsOn: string | null; note: string };
}

export const listPromotions = () => api<{ promotions: Promotion[] }>('/promotions');

export const listPromotionSuggestions = () =>
  api<{ suggestions: PromotionSuggestion[] }>('/promotions/suggestions');

export interface CreatePromotionInput {
  productId: string;
  kind: PromotionKind;
  value: number;
  startsOn: string;
  endsOn?: string | null;
  note?: string;
}

export const createPromotion = (input: CreatePromotionInput) =>
  api<Promotion>('/promotions', { method: 'POST', body: JSON.stringify(input) });

export const updatePromotion = (id: string, input: Partial<CreatePromotionInput>) =>
  api<void>(`/promotions/${id}`, { method: 'PATCH', body: JSON.stringify(input) });

/** Stops a running promotion. It stays on the record — see the API's comment. */
export const endPromotion = (id: string) => api<void>(`/promotions/${id}/end`, { method: 'POST' });

/** Only possible while no sale has been made under it; 409 otherwise. */
export const deletePromotion = (id: string) => api<void>(`/promotions/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------- analytics

export interface AnalyticsResponse {
  today: string;
  /** The panels added on 11 October 2026; computed in `packages/core`. */
  insights: StockInsights;
  /**
   * What each treatment made after its products, over the chosen period.
   * Null when the plan has no Targets, like `targetProgress`.
   */
  treatments: TreatmentProfitReport | null;
  windowDays: number;
  atRiskWeeks: number;
  stock: {
    totalValuePence: number;
    totalUnits: number;
    productCount: number;
    atRiskValuePence: number;
    reorderCount: number;
    averageValuePence: number | null;
    byClassification: { label: string; valuePence: number; units: number; sharePercent: number }[];
  };
  retail: {
    revenuePence: number;
    grossProfitPence: number;
    marginPercent: number | null;
    unitsSold: number;
    best: { productId: string; productName: string; unitsSold: number; revenuePence: number }[];
    notSelling: {
      productId: string;
      productName: string;
      qtyOnHand: number;
      unitCostPence: number;
    }[];
  };
  months: {
    monthKey: string;
    previousMonthKey: string;
    top: {
      productId: string;
      productName: string;
      qty: number;
      revenuePence: number;
      changeQty: number;
    }[];
    quietest: {
      productId: string;
      productName: string;
      qty: number;
      revenuePence: number;
      changeQty: number;
    }[];
  };
  /**
   * This month against the clinic's target. Null below Premium — Analytics is
   * a Pro page, Targets data is not. Same figures as `GET /targets`.
   */
  targetProgress: {
    monthKey: string;
    configured: boolean;
    progress: {
      targetPence: number;
      earnedPence: number;
      treatmentsPence: number;
      retailSalesPence: number;
      treatmentsDone: number;
      stockCostPence: number;
      afterStockPence: number;
    };
    pace: {
      deltaPence: number | null;
      onPace: boolean;
      projectedPence: number | null;
      projectedHitsTarget: boolean;
      dayOfMonth: number;
      daysInMonth: number;
    };
    earnedPercentOfTarget: number | null;
    averagePerTreatmentPence: number | null;
    rows: {
      treatmentId: string;
      name: string;
      pricePence: number;
      count: number;
      revenuePence: number;
      percentOfTop: number;
    }[];
  } | null;
  promotions: {
    results: {
      promotionId: string;
      productName: string;
      offer: string;
      unitsSold: number;
      revenuePence: number;
      discountGivenPence: number;
    }[];
    totalUnits: number;
    totalRevenuePence: number;
    totalDiscountPence: number;
  };
}

export const getAnalytics = (days: InsightPeriod = 90) =>
  api<AnalyticsResponse>(`/analytics?days=${days}`);

// ------------------------------------------------------------------ targets

export interface TargetTreatment {
  id: string;
  name: string;
  pricePence: number;
  productIds: string[];
  /** How much of each product one treatment uses, in hundredths. */
  recipe: { productId: string; amountHundredths: number }[];
  count: number;
  neededForTarget: number | null;
  perWeek: number | null;
  availability: { key: 'ok' | 'low' | 'blocked'; missing: string[]; low: string[] };
}

export interface TargetsResponse {
  today: string;
  monthKey: string;
  profitGoalPence: number;
  costs: { id: string; label: string; amountPence: number }[];
  progress: {
    costsPence: number;
    profitGoalPence: number;
    targetPence: number;
    /** Treatments plus retail sales. */
    earnedPence: number;
    treatmentsPence: number;
    retailSalesPence: number;
    treatmentsDone: number;
    /** What the products used and sold this month cost. */
    stockCostPence: number;
    afterStockPence: number;
    toBreakEvenPence: number;
    toTargetPence: number;
    profitSoFarPence: number;
    costsCovered: boolean;
    targetReached: boolean;
    earnedPercent: number;
    breakEvenPercent: number;
    targetPercent: number;
  };
  pace: {
    deltaPence: number | null;
    onPace: boolean;
    projectedPence: number | null;
    projectedHitsTarget: boolean;
    dayOfMonth: number;
    daysInMonth: number;
  };
  treatments: TargetTreatment[];
  products: {
    id: string;
    name: string;
    /** Unopened packs on the shelf. */
    qtyOnHand: number;
    unitCostPence: number;
    /** What one pack holds, in hundredths. 100 means "a pack is one unit". */
    packContentsHundredths: number;
    /** "units", "ml". Null when recipes measure this product in whole packs. */
    contentUnit: string | null;
    /** Left in a pack that is already open, in hundredths. */
    openHundredths: number;
  }[];
}

export const getTargets = () => api<TargetsResponse>('/targets');

export const saveTargets = (input: {
  profitGoalPence?: number;
  costs?: { label: string; amountPence: number }[];
  treatments?: {
    id?: string;
    name: string;
    pricePence: number;
    productIds: string[];
    recipe?: { productId: string; amountHundredths: number }[];
  }[];
}) => api<void>('/targets', { method: 'PUT', body: JSON.stringify(input) });

/**
 * Log one treatment: takes its recipe off the shelf and counts toward the
 * month. Refused with a 409 when a product is short; `substitutions` says
 * "use this product instead, this time".
 */
export const logTreatmentEvent = (
  treatmentId: string,
  substitutions: { productId: string; withProductId: string }[] = [],
) =>
  api<{ eventId: string; costPence: number }>(`/targets/treatments/${treatmentId}/log`, {
    method: 'POST',
    body: JSON.stringify({ substitutions }),
  });

/** Undo the most recent logging of a treatment this month. */
export const undoTreatment = (treatmentId: string) =>
  api<{ stockReturned: boolean }>(`/targets/treatments/${treatmentId}/undo`, {
    method: 'POST',
    body: JSON.stringify({}),
  });


// -------------------------------------------------------------------- billing

/**
 * Starts a Stripe Checkout session and returns where to send the browser.
 *
 * Nothing about the clinic's plan changes here — Stripe's webhook does that
 * when the payment actually succeeds. See apps/api/src/modules/billing.ts.
 */
export const startCheckout = (plan: Plan, cycle: Cycle) =>
  api<{ url: string }>('/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan, cycle }),
  });

/** A link into Stripe's Customer Portal: cards, invoices, VAT, cancellation. */
export const openBillingPortal = () => api<{ url: string }>('/billing/portal', { method: 'POST' });

// ---------------------------------------------------------------- plan ahead

export interface PackageItem {
  productId: string;
  qty: number;
  name: string;
  /** Sent so the client can cost a plan without a second round trip. */
  unitCostPence: number;
}

export interface TreatmentPackage {
  id: string;
  name: string;
  pricePence: number;
  plannedCount: number;
  month: string;
  /** Whether it has been sent to This month, so the button is a toggle. */
  sentToThisMonth: boolean;
  items: PackageItem[];
}

export interface PackageInputBody {
  name: string;
  pricePence: number;
  plannedCount: number;
  month: string;
  items: { productId: string; qty: number }[];
}

export const getPackages = (month: string) =>
  api<{ month: string; packages: TreatmentPackage[] }>(`/packages?month=${month}`);

export const createPackage = (body: PackageInputBody) =>
  api<{ id: string }>('/packages', { method: 'POST', body: JSON.stringify(body) });

export const updatePackage = (id: string, body: PackageInputBody) =>
  api<{ ok: true }>(`/packages/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

export const deletePackage = (id: string) => api<void>(`/packages/${id}`, { method: 'DELETE' });

/** Copies into another month. The copy diverges freely from the original. */
export const copyPackage = (id: string, month: string) =>
  api<{ id: string }>(`/packages/${id}/copy`, {
    method: 'POST',
    body: JSON.stringify({ month }),
  });

/** Toggles: creates a treatment from the package, or archives the one it made. */
export const sendPackageToThisMonth = (id: string) =>
  api<{ sentToThisMonth: boolean }>(`/packages/${id}/send`, { method: 'POST' });
